/**
 * Window a strategy's equity curve to a date range and rebase it to a starting
 * capital, the way the Overview's growth curve does (shared by /embed and the
 * public results page, so they can't drift apart).
 */
import {
  applyLinearLeverageToSeries,
  buildEquityPortfolioSeries,
} from './vectorCapitalScenario';

/**
 * Window a strategy curve so the window start = capital, matching the
 * dashboard's per-model semantics:
 *  - 'fixed' (futures journals, Fyntrak-style curves): additive — dollar moves
 *    scale on the strategy's original base.
 *  - 'compounded' (Kairos QLD/TQQQ price-like curves): ratio — a curve at 8×
 *    its base must NOT have its recent dollar swings divided by the 2022 base.
 * contracts scales a 'fixed' curve's dollar moves (1 = as on the Overview).
 */
export function windowStrategyPoints(points, startDate, capital, model = 'fixed', contracts = 1, originalBase = null) {
  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length < 2) return [];
  // 'fixed' moves scale on the curve's original base (its first value, even when the start is cut off).
  const base = originalBase > 0 ? originalBase : (sorted[0].equity > 0 ? sorted[0].equity : 100000);
  let anchorIdx = 0;
  if (startDate) {
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      if (sorted[i].date < startDate) { anchorIdx = i; break; }
    }
  }
  const anchorEq = sorted[anchorIdx].equity;
  const compounded = model === 'compounded' && anchorEq > 0;
  return sorted.slice(anchorIdx).map((p) => ({
    date: p.date,
    value: Math.round((compounded
      ? capital * (p.equity / anchorEq)
      : capital * (1 + (contracts * (p.equity - anchorEq)) / base)) * 100) / 100,
  }));
}

/** Rebuild portfolio_blend curves for the selected range — same path as Overview. */
export function windowPortfolioBlendSeries(portfolioBuild, startDate, capital) {
  const {
    legs, leverage, compoundMethod, weighting, leverageMode, legReturns,
  } = portfolioBuild || {};
  if (!Array.isArray(legs) || !legs.length || !legReturns) return [];
  const strategyByName = new Map(
    Object.entries(legReturns).map(([name, returns]) => [name, { returns }]),
  );
  const range = startDate ? { startDate } : {};
  const effLeverage = Number.isFinite(Number(leverage)) ? Number(leverage) : 1;
  let series;
  if (leverageMode === 'linear' && effLeverage !== 1) {
    const oneXSlice = buildEquityPortfolioSeries(
      legs, 1, strategyByName, range, capital, compoundMethod, weighting,
    );
    series = applyLinearLeverageToSeries(oneXSlice, effLeverage, oneXSlice, capital);
  } else {
    series = buildEquityPortfolioSeries(
      legs, effLeverage, strategyByName, range, capital, compoundMethod, weighting,
    );
  }
  return series
    .filter((p) => p?.date && Number.isFinite(Number(p.normalized)))
    .map((p) => ({ date: p.date, value: Number(p.normalized) }));
}
